import { Store as $store } from '@store';
import { computed } from 'vue';
import {
  addMilliseconds, subMilliseconds, differenceInYears, parse,
} from 'date-fns';
import { getTimezoneOffset } from 'date-fns-tz';
import { format } from 'timeago.js';

export const currentUserTimezone = computed(() => $store.getters['app/user'].timezone || 'UTC');

export const currentBrowserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Create Date from UTC dateString with user defined timezone offset
 *
 * @param {String|Number} dateString
 */
export function getCurrentUserDate(dateString) {
  const date = new Date(dateString);

  return addMilliseconds(
    subMilliseconds(date, getTimezoneOffset(currentBrowserTimezone, date)),
    getTimezoneOffset(currentUserTimezone.value, date),
  );
}

export function getCurrentUserTimezoneOffset(dateString) {
  const date = new Date(dateString);

  return getTimezoneOffset(currentUserTimezone.value, date);
}

export function getCurrentBrowserTimezoneOffset(dateString) {
  const date = new Date(dateString);

  return getTimezoneOffset(currentBrowserTimezone.value, date);
}

/**
 * Create Date from UTC dateString and shows UTC date in the current browser timezone
 *
 * @param {String} dateString
 */
export function getUtcDate(dateString) {
  const date = new Date(dateString);
  return subMilliseconds(date, getTimezoneOffset(currentBrowserTimezone, date));
}

export function getDefaultLocaleDateString(date) {
  if (!date) {
    return null;
  }

  return new Date(date).toLocaleDateString('en-US');
}

/**
 * Get default locale Date string from UTC date string
 * Shows date as it is from string, but in locale format
 *
 * @param {String} date
 */
export function getDefaultLocaleDateStringUTC(date) {
  if (!date) {
    return null;
  }

  return getDefaultLocaleDateString(getUtcDate(date));
}

export function getDefaultLocaleTimeString(date) {
  return new Date(date).toLocaleTimeString('en-US');
}

export function getDefaultLocaleDatetimeString(date) {
  return new Date(date).toLocaleString('en-US');
}

export function minutesToStr(min) {
  return new Date(min * 60 * 1000).toISOString().substring(11, 16);
}

export function secondsToHumanReadableTime(seconds) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds - (hours * 3600)) / 60);
  seconds = seconds - (hours * 3600) - (minutes * 60);
  if (hours) {
    if (minutes) {
      return `${hours}h ${minutes}m`;
    }
    return `${hours}h`;
  }
  if (minutes) {
    return `${minutes}m`;
  }
  return `${seconds}s`;
}

export const calculateAge = (dob) => {
  const date = parse(dob, 'yyyy-MM-dd', new Date());

  return differenceInYears(new Date(), date);
};

export const timeAgo = (date) => format(date);

export const timeStringTo12h = (timeString) => new Date(`1970-01-01T${timeString}Z`)
  .toLocaleTimeString('en-US',
    { timeZone: 'UTC', hour12: true, hour: 'numeric', minute: 'numeric' });

export const datetimeFormat = 'yyyy-MM-dd\'T\'HH:mm:ssxxx';
export const datetimeSimpleFormat = 'yyyy-MM-dd HH:mm:ss';
export const dateFormat = 'yyyy-MM-dd';
export const dateFormatUS = 'MM/dd/yyyy';
